import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileText, List, ClipboardCheck, Calculator } from 'lucide-react';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';
import { trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CTA configurations for each free tool
const TOOL_CTAS = {
  score: {
    icon: Calculator,
    title: 'Get Your AI Accountability Score',
    description: 'Six questions. Instant score. Clear next steps.',
    cta: 'Get Your Free Score',
    link: '/assessment/form'
  },
  policy: {
    icon: FileText,
    title: 'Generate Your AI Policy',
    description: 'Create a customized AI Acceptable Use Policy for your organization.',
    cta: 'Generate Your Policy',
    link: '/tools/policy-generator'
  },
  inventory: {
    icon: List,
    title: 'Build Your AI Inventory',
    description: 'Document every AI tool your organization uses.',
    cta: 'Build Your Inventory',
    link: '/tools/inventory-builder'
  },
  checklist: {
    icon: ClipboardCheck,
    title: 'Get Your Governance Checklist',
    description: '12 questions to identify your governance gaps.',
    cta: 'Get Your Checklist',
    link: '/tools/governance-checklist'
  }
};

function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await fetch(`${API_URL}/api/articles/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Article not found');
        } else {
          setError('Failed to load article');
        }
        return;
      }
      const data = await response.json();
      setArticle(data);
      
      // Track article view for Phase 6 funnel analytics
      trackEvent('content_viewed', {
        content_type: 'article',
        content_slug: slug,
        content_title: data.title,
        category: data.category
      });
    } catch (err) {
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const toolCta = article ? TOOL_CTAS[article.free_tool_cta] || TOOL_CTAS.score : TOOL_CTAS.score;
  const ToolIcon = toolCta.icon;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/articles" 
            className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#102A43] mb-8 text-sm"
          >
            <ArrowLeft size={16} />
            Back to Articles
          </Link>

          {loading && (
            <div className="text-center py-12">
              <p className="text-[#64748B]">Loading article...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button 
                onClick={() => navigate('/articles')}
                className="text-[#2F80ED] hover:underline"
              >
                Return to articles
              </button>
            </div>
          )}

          {!loading && !error && article && (
            <>
              {/* Article Header */}
              <div className="mb-8">
                <h1 className="text-[36px] font-bold text-[#102A43] mb-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-[18px] text-[#64748B]">
                  {article.meta_description}
                </p>
              </div>

              {/* Tool CTA Box - Top */}
              <div className="bg-[#2F80ED]/5 border border-[#2F80ED]/20 rounded-[10px] p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <ToolIcon className="text-[#2F80ED]" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#102A43] mb-1">{toolCta.title}</p>
                    <p className="text-[14px] text-[#64748B] mb-3">{toolCta.description}</p>
                    <Link 
                      to={toolCta.link}
                      className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-5 py-2 rounded-[7px] hover:bg-[#2563eb] transition-colors text-[14px]"
                    >
                      {toolCta.cta}
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Article Body */}
              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 mb-8">
                {article.body ? (
                  <div 
                    className="prose prose-zinc max-w-none prose-headings:text-[#102A43] prose-p:text-[#334E68] prose-a:text-[#2F80ED]"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body) }}
                  />
                ) : (
                  <div className="text-center py-12 text-[#64748B]">
                    <p className="mb-4">This article is coming soon.</p>
                    <p className="text-sm">
                      In the meantime, try our free{' '}
                      <Link to={toolCta.link} className="text-[#2F80ED] hover:underline">
                        {toolCta.title.toLowerCase()}
                      </Link>
                      .
                    </p>
                  </div>
                )}
              </div>

              {/* Tool CTA Box - Bottom */}
              <div className="bg-[#102A43] rounded-[10px] p-8 text-center">
                <h2 className="text-[24px] font-bold text-white mb-3">
                  Ready to take action?
                </h2>
                <p className="text-[16px] text-[#94A3B8] mb-6">
                  {toolCta.description}
                </p>
                <Link 
                  to={toolCta.link}
                  className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                >
                  {toolCta.cta}
                  <ArrowRight size={18} />
                </Link>
              </div>

              {/* Documentation System Upsell */}
              <div className="mt-8 bg-white border border-[#E2E8F0] rounded-[10px] p-6">
                <p className="text-[15px] text-[#102A43] mb-4">
                  <strong>Need the full documentation system?</strong> The AI Accountability Documentation System 
                  includes 8 templates — everything you need to document your AI governance posture. $97 one-time.
                </p>
                <Link 
                  to="/documentation-system"
                  className="text-[#2F80ED] font-medium text-[14px] hover:underline flex items-center gap-1"
                >
                  Get the Documentation System <ArrowRight size={14} />
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default ArticleDetail;
ArticleDetail;
